/**
 * 语文听写 —— **第三步：听写会话与家长录音**的接口层。
 *
 * 第一步（字表录入）的端点在 `./dictation.ts`，本文件只装第三步新增的 11 条：
 *
 * | 方法 | 路径 | 说明 |
 * | --- | --- | --- |
 * | GET   | `/students/:id/dictation/sessions/tempo-default` | 上次用过的节奏（表单预填） |
 * | GET   | `/students/:id/dictation/sessions` | 历史会话列表（含题目数） |
 * | POST  | `/students/:id/dictation/sessions` | **布置听写**（服务端取词 → 落库成题目） |
 * | GET   | `/students/:id/dictation/sessions/:sessionId` | 读会话（含题目 + 朗读来源） |
 * | PATCH | `/students/:id/dictation/sessions/:sessionId` | 改节奏 / 标题 / 状态 |
 * | POST  | `/students/:id/dictation/sessions/:sessionId/progress` | 记录播放进度 |
 * | DELETE| `/students/:id/dictation/sessions/:sessionId` | 删会话 |
 * | GET   | `/students/:id/dictation/audios` | 已经录过音的词 |
 * | GET   | `/students/:id/dictation/audios/:wordId` | 取一段录音（`dataUrl` 直接喂 `<audio>`） |
 * | PUT   | `/students/:id/dictation/audios/:wordId` | 存 / 覆盖一段录音 |
 * | DELETE| `/students/:id/dictation/audios/:wordId` | 删录音（回到浏览器语音） |
 *
 * ## 这一层同样遵守听写模块的铁律：**显式解码，绝不静默兜底**
 *
 * 见 `./dictation.ts` 顶部。这里额外强调三件**第三步特有**的事：
 *
 *   1. **`hasAudio` 必须来自显式布尔**，不能靠"`audioPath` 是不是空串"推：
 *      没有录音时后端给的是 `null`，而"字段拿不到"也是 `null` ——
 *      两者在界面上是同一件事（都走 TTS），但**日志与排查**必须分得开。
 *      所以解码后保留 `null`（拿不到）与 `false`（确实没有）的区别。
 *   2. **`spokenSource` 是后端当场派生的**（有录音 = `parent_audio`）。
 *      前端**不自己重算**（不"看 audioPath 猜"）：重算就会出现"界面说用录音、
 *      播放器却走了 TTS"这种最难查的不一致。
 *   3. **录音接口的 404 是正常答案**（"这个词还没录"），不是错误：
 *      `getAudio` 对它返回 `null`，其它状态码照旧抛（401/403 不能被吞）。
 */
import { toOptionalNumber } from './shape';
import { api } from './client';
import {
  DEFAULT_DICTATION_TEMPO,
  base64DecodedBytes,
  type DictationTempoConfig,
} from '@fht/shared';

// ---------------------------------------------------------------- 工具

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function toStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function toOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function toBooleanOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function toBooleanOrFalse(value: unknown): boolean {
  return value === true;
}

/** 对象数组的解码：键不是数组 → `null`（**不是空数组**）。元素解码失败记进 `missing`。 */
function decodeList<T>(
  source: unknown,
  key: string,
  decodeOne: (raw: Record<string, unknown>, missing: string[]) => T,
): { items: T[] | null; missing: string[] } {
  if (!isRecord(source) || !Array.isArray(source[key])) return { items: null, missing: [key] };
  const missing: string[] = [];
  const items = (source[key] as unknown[]).map((raw) =>
    decodeOne(isRecord(raw) ? raw : {}, missing),
  );
  return { items, missing };
}

// ---------------------------------------------------------------- 视图类型

/** 朗读来源：家长录音 / 浏览器语音合成。 */
export type SpokenSourceView = 'parent_audio' | 'tts';

/** 批改结果：对 / 写错 / **没写出来**。`null` = 还没批改。 */
export type DictationMarkView = 'correct' | 'wrong' | 'missing';

/**
 * 这个词**现在**在这个孩子的错词本里的情况。
 *
 * ⚠️ 三种取值必须分得开（本项目反复踩过的坑）：
 *   · 对象 —— 在错词本里；
 *   · `null` —— **确实不在**错词本里（没错过，或已经掌握）；
 *   · `undefined` —— **字段没拿到**（后端没给 / 响应形状变了）。
 */
export interface QuestionMistakeView {
  /** 累计写错次数。 */
  wrongCount: number;
  /** 累计没写出来次数。 */
  missingCount: number;
  /** 下次该复习的时间（已掌握的是 `null`）。 */
  nextReviewAt: string | null;
  /** 现在是否已到期（**后端算的**，不信浏览器时钟）。 */
  due: boolean | null;
}

/** 会话里的一道题。 */
export interface DictationQuestionView {
  id: string;
  /** 题号，从 1 开始（"第 3 题 / 共 20 题"）。 */
  position: number;
  /** 指向字表里的词；词被删过则是 `null`（历史会话仍在）。 */
  wordId: string | null;
  wordText: string;
  pinyin: string;
  status: string;
  /**
   * **批改**结果。
   *
   * ⚠️ `null` 与 `undefined` 是两件事：
   *   · `undefined` —— 字段没拿到（老后端 / 响应形状不对）；
   *   · `null` —— 拿到了，值是"**还没批改**"。
   * 把前者显示成"还没批改"会让家长以为批改没保存上（反复重提交）。
   */
  mark: DictationMarkView | null | undefined;
  /**
   * 这个词在错词本里的情况（`undefined` = 没拿到，`null` = 确实不在）。
   */
  mistake: QuestionMistakeView | null | undefined;
  spokenSource: SpokenSourceView;
  /** 有录音时：`/students/:id/dictation/audios/:wordId`（**不含** `/api/v1`）。 */
  audioPath: string | null;
  audioMimeType: string | null;
  audioBytes: number | null;
  audioDurationMs: number | null;
  /**
   * 这一题有没有家长录音。
   * `null` = **字段拿不到**（后端没给）—— 与 `false`（确实没录）分开。
   */
  hasAudio: boolean | null;
  /** 用 TTS 时念的文字（**永远是词本身**）。 */
  spokenText: string;
}

/** 一次听写会话。 */
export interface DictationSessionView {
  id: string;
  studentId: string;
  title: string;
  status: string;
  /** 选词条件快照（后端把补齐默认值后的条件存在这里）。 */
  pickCriteria: Record<string, unknown>;
  tempo: DictationTempoConfig;
  questionCount: number;
  /**
   * 所属的**批量任务**（迁移 0020）。`null` = 独立的一次听写。
   *
   * 听写历史靠它把"第 N 批"挂回它属于的那个任务下面 ——
   * 用户原话："不然以后听写历史一多，多少批、多少次，都不知道哪一批属于哪个任务。"
   */
  assignmentId: string | null;
  /** 第几批（1 起）；独立听写是 `null`。 */
  batchNo: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DictationSessionDetailView extends DictationSessionView {
  /** `null` = 题目列表没拿到（**不是"没有题目"**）。 */
  questions: DictationQuestionView[] | null;
}

/** 一段录音的元信息 + 可直接播放的 data URL。 */
export interface DictationAudioView {
  wordId: string;
  wordText: string;
  mimeType: string;
  byteSize: number | null;
  durationMs: number | null;
  updatedAt: string | null;
  /** `data:audio/webm;base64,...` —— 直接喂 `<audio src>`。 */
  dataUrl: string;
}

/** 已经录过音的词（清单页用）。 */
export interface AudioListItemView {
  wordId: string;
  wordText: string;
  mimeType: string;
  byteSize: number | null;
  durationMs: number | null;
  updatedAt: string | null;
}

// ---------------------------------------------------------------- 解码

/** 节奏的解码：**缺项补默认值**（老行/手工改过的行可能缺字段，不能让界面拿到 undefined）。 */
export function decodeTempo(raw: unknown): DictationTempoConfig {
  const r = isRecord(raw) ? raw : {};
  const num = (key: keyof DictationTempoConfig, fallback: number): number => {
    const value = toOptionalNumber(r[key]);
    return value === null ? fallback : value;
  };
  return {
    repeatCount: num('repeatCount', DEFAULT_DICTATION_TEMPO.repeatCount),
    gapSeconds: num('gapSeconds', DEFAULT_DICTATION_TEMPO.gapSeconds),
    betweenWordsSeconds: num('betweenWordsSeconds', DEFAULT_DICTATION_TEMPO.betweenWordsSeconds),
  };
}

/** 判定字符串 → 视图类型。认不出来的（含空串）返回 `undefined` = **字段坏了**。 */
export function decodeMark(raw: unknown): DictationMarkView | null | undefined {
  if (raw === null) return null;
  if (raw === 'correct' || raw === 'wrong' || raw === 'missing') return raw;
  return undefined;
}

function decodeQuestionMistake(raw: unknown): QuestionMistakeView | null | undefined {
  if (raw === null) return null;
  if (!isRecord(raw)) return undefined;
  const wrongCount = toOptionalNumber(raw['wrongCount']);
  const missingCount = toOptionalNumber(raw['missingCount']);
  if (wrongCount === null || missingCount === null) return undefined;
  return {
    wrongCount,
    missingCount,
    nextReviewAt: toOptionalString(raw['nextReviewAt']),
    due: toBooleanOrNull(raw['due']),
  };
}

export function decodeQuestion(raw: unknown): DictationQuestionView {
  const r = isRecord(raw) ? raw : {};
  const wordText = toStringOrEmpty(r['wordText']);
  return {
    id: toStringOrEmpty(r['id']),
    position: toOptionalNumber(r['position']) ?? 0,
    wordId: toOptionalString(r['wordId']),
    wordText,
    pinyin: toStringOrEmpty(r['pinyin']),
    status: toStringOrEmpty(r['status']),
    mark: decodeMark(r['mark']),
    mistake: decodeQuestionMistake(r['mistake']),
    // 认不出来的来源一律当 `tts`（保守：宁可让家长补录，也不要"以为在放录音其实在读 TTS"）
    spokenSource: r['spokenSource'] === 'parent_audio' ? 'parent_audio' : 'tts',
    audioPath: toOptionalString(r['audioPath']),
    audioMimeType: toOptionalString(r['audioMimeType']),
    audioBytes: toOptionalNumber(r['audioBytes']),
    audioDurationMs: toOptionalNumber(r['audioDurationMs']),
    hasAudio: toBooleanOrNull(r['hasAudio']),
    // 拿不到 `spokenText` 时退回词本身（后端也是这么给的）
    spokenText: toStringOrEmpty(r['spokenText']) || wordText,
  };
}

export function decodeSession(raw: unknown): DictationSessionView {
  const r = isRecord(raw) ? raw : {};
  return {
    id: toStringOrEmpty(r['id']),
    studentId: toStringOrEmpty(r['studentId']),
    title: toStringOrEmpty(r['title']),
    status: toStringOrEmpty(r['status']),
    pickCriteria: isRecord(r['pickCriteria']) ? r['pickCriteria'] : {},
    tempo: decodeTempo(r['tempo']),
    questionCount: toOptionalNumber(r['questionCount']) ?? 0,
    assignmentId: toOptionalString(r['assignmentId']),
    batchNo: toOptionalNumber(r['batchNo']),
    startedAt: toOptionalString(r['startedAt']),
    finishedAt: toOptionalString(r['finishedAt']),
    createdAt: toStringOrEmpty(r['createdAt']),
    updatedAt: toStringOrEmpty(r['updatedAt']),
  };
}

export function decodeSessionDetail(raw: unknown): DictationSessionDetailView {
  const base = decodeSession(raw);
  const { items } = decodeList(raw, 'questions', (row) => decodeQuestion(row));
  return {
    ...base,
    questions: items === null ? null : items.slice().sort((a, b) => a.position - b.position),
  };
}

export function decodeAudio(raw: unknown): DictationAudioView {
  const r = isRecord(raw) ? raw : {};
  return {
    wordId: toStringOrEmpty(r['wordId']),
    wordText: toStringOrEmpty(r['wordText']),
    mimeType: toStringOrEmpty(r['mimeType']),
    byteSize: toOptionalNumber(r['byteSize']),
    durationMs: toOptionalNumber(r['durationMs']),
    updatedAt: toOptionalString(r['updatedAt']),
    dataUrl: toStringOrEmpty(r['dataUrl']),
  };
}

function decodeAudioListItem(raw: Record<string, unknown>): AudioListItemView {
  return {
    wordId: toStringOrEmpty(raw['wordId']),
    wordText: toStringOrEmpty(raw['wordText']),
    mimeType: toStringOrEmpty(raw['mimeType']),
    byteSize: toOptionalNumber(raw['byteSize']),
    durationMs: toOptionalNumber(raw['durationMs']),
    updatedAt: toOptionalString(raw['updatedAt']),
  };
}

// ---------------------------------------------------------------- 端点：会话

/** 上次用过的节奏（表单预填）。没有任何会话时后端给默认值。 */
export async function fetchTempoSuggestion(studentId: string): Promise<{
  tempo: DictationTempoConfig;
  source: 'last_session' | 'default';
}> {
  const data = await api.get<unknown>(`/students/${studentId}/dictation/sessions/tempo-default`);
  const r = isRecord(data) ? data : {};
  return {
    tempo: decodeTempo(r['tempo']),
    source: r['source'] === 'last_session' ? 'last_session' : 'default',
  };
}

/** 历史会话列表。`sessions === null` = 这份数据没拿到（不是"还没有会话"）。 */
export async function listSessions(
  studentId: string,
  query: { status?: string; limit?: number; offset?: number } = {},
): Promise<{ sessions: DictationSessionView[] | null; total: number | null; missing: string[] }> {
  const data = await api.get<unknown>(`/students/${studentId}/dictation/sessions`, {
    ...(query.status !== undefined ? { status: query.status } : {}),
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
  });
  const decoded = decodeList(data, 'sessions', (raw) => decodeSession(raw));
  return {
    sessions: decoded.items,
    total: isRecord(data) ? toOptionalNumber(data['total']) : null,
    missing: decoded.missing,
  };
}

/**
 * 布置听写：把选词条件与节奏交给后端，**取词与出题都在服务端做**。
 *
 * 前端**不**把词列表贴进来：那会变成第二套取词逻辑（"预览说 20 个、布置出来 18 个"
 * 就再也说不清是哪边错了）。
 */
export interface CreateSessionPayload {
  /** 取词条件（与 `PickPayload` 同形，原样透传给 `pickDictationWords`）。 */
  pick: Record<string, unknown>;
  title?: string;
  tempo?: DictationTempoConfig;
}

export async function createSession(
  studentId: string,
  payload: CreateSessionPayload,
): Promise<DictationSessionDetailView> {
  const data = await api.post<unknown>(`/students/${studentId}/dictation/sessions`, payload);
  return decodeSessionDetail(data);
}

/** 读一个会话（含题目）。会话不属于这个孩子时后端给 404。 */
export async function getSession(
  studentId: string,
  sessionId: string,
): Promise<DictationSessionDetailView> {
  const data = await api.get<unknown>(
    `/students/${studentId}/dictation/sessions/${sessionId}`,
  );
  return decodeSessionDetail(data);
}

export interface UpdateSessionPayload {
  title?: string;
  tempo?: DictationTempoConfig;
  /** ⚠️ **没有** `graded`：批改是第四步，本轮写不进去。 */
  status?: 'pending' | 'in_progress' | 'completed';
}

export async function updateSession(
  studentId: string,
  sessionId: string,
  payload: UpdateSessionPayload,
): Promise<DictationSessionDetailView> {
  const data = await api.patch<unknown>(
    `/students/${studentId}/dictation/sessions/${sessionId}`,
    payload,
  );
  return decodeSessionDetail(data);
}

/** 批量记录播放进度（读过 / 跳过）。⚠️ 这里**没有**对错的入口。 */
export async function saveProgress(
  studentId: string,
  sessionId: string,
  questions: { questionId: string; status: 'pending' | 'played' | 'skipped' }[],
): Promise<{ updated: number; skippedUnknown: number }> {
  const data = await api.post<unknown>(
    `/students/${studentId}/dictation/sessions/${sessionId}/progress`,
    { questions },
  );
  const r = isRecord(data) ? data : {};
  return {
    updated: toOptionalNumber(r['updated']) ?? 0,
    skippedUnknown: toOptionalNumber(r['skippedUnknown']) ?? 0,
  };
}

/** 删会话（题目随外键一起删）。`false` = 本来就不在了。 */
export async function deleteSession(studentId: string, sessionId: string): Promise<boolean> {
  const data = await api.del<unknown>(`/students/${studentId}/dictation/sessions/${sessionId}`);
  return isRecord(data) && data['deleted'] === true;
}

// ---------------------------------------------------------------- 端点：录音

/** 已经录过音的词（清单）。 */
export async function listAudios(
  studentId: string,
  query: { limit?: number; offset?: number } = {},
): Promise<{ audios: AudioListItemView[] | null; total: number | null; missing: string[] }> {
  const data = await api.get<unknown>(`/students/${studentId}/dictation/audios`, {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
  });
  const decoded = decodeList(data, 'audios', (raw) => decodeAudioListItem(raw));
  return {
    audios: decoded.items,
    total: isRecord(data) ? toOptionalNumber(data['total']) : null,
    missing: decoded.missing,
  };
}

/**
 * 取一个词的录音。
 *
 * 返回 `null` = **这个词没有录音**（后端 404 `DICTATION_AUDIO_NOT_FOUND`）——
 * 这是一个正常答案（播放器据此走 TTS），**不是错误**。
 * 401 / 403 / 网络错误一律照旧抛出（权限错误不能被吞成"没有录音"）。
 */
export async function getAudio(
  studentId: string,
  wordId: string,
): Promise<DictationAudioView | null> {
  try {
    const data = await api.get<unknown>(
      `/students/${studentId}/dictation/audios/${wordId}`,
      { format: 'dataUrl' },
    );
    return decodeAudio(data);
  } catch (error) {
    if (isAudioMissing(error)) return null;
    throw error;
  }
}

/** 判断一个异常是不是"这个词还没录音"（404 / `DICTATION_AUDIO_NOT_FOUND`）。 */
export function isAudioMissing(error: unknown): boolean {
  if (!isRecord(error)) return false;
  return error['code'] === 'DICTATION_AUDIO_NOT_FOUND' || error['status'] === 404;
}

export interface UploadAudioPayload {
  mimeType: string;
  /** **纯 base64**，不带 `data:` 前缀（带了后端会拒 —— 见 shared 的 schema）。 */
  audioBase64: string;
  /** 原始字节数（`Blob.size`）。 */
  byteSize?: number;
  /** 真实时长（毫秒）；测不出来就**不传**（存 NULL，与"0 毫秒"是两件事）。 */
  durationMs?: number | null;
}

export interface SavedAudioView {
  wordId: string;
  mimeType: string;
  byteSize: number;
  durationMs: number | null;
  base64Length: number;
  replaced: boolean;
}

export async function putAudio(
  studentId: string,
  wordId: string,
  payload: UploadAudioPayload,
): Promise<SavedAudioView> {
  const data = await api.put<unknown>(
    `/students/${studentId}/dictation/audios/${wordId}`,
    payload,
  );
  const r = isRecord(data) ? data : {};
  return {
    wordId: toStringOrEmpty(r['wordId']),
    mimeType: toStringOrEmpty(r['mimeType']),
    byteSize: toOptionalNumber(r['byteSize']) ?? 0,
    durationMs: toOptionalNumber(r['durationMs']),
    base64Length: toOptionalNumber(r['base64Length']) ?? 0,
    replaced: toBooleanOrFalse(r['replaced']),
  };
}

/** 删一个词的录音（`false` = 本来就没录过）。 */
export async function deleteAudio(studentId: string, wordId: string): Promise<boolean> {
  const data = await api.del<unknown>(`/students/${studentId}/dictation/audios/${wordId}`);
  return isRecord(data) && data['deleted'] === true;
}

// ---------------------------------------------------------------- 端点：批改（第四步）

/** 提交批改的结果（含更新后的会话详情）。 */
export interface GradeResultView {
  sessionId: string;
  status: string;
  /** 这次**真正改了判定**的题数（0 = 这次提交什么都没改 —— 幂等的证据）。 */
  changedQuestions: number;
  /** 判定与上次完全一样、被跳过的题数。 */
  unchangedQuestions: number;
  /** 这次触及的错词数。 */
  touchedMistakes: number;
  /** 这次**新**进错词本的词数。 */
  newMistakes: number;
  /** 这次被消解（连续答对到阈值）的词数。 */
  resolvedMistakes: number;
  /**
   * 这次因为"判定改成对"而**整条撤掉**的错词记录数（2026-10-29）。
   *
   * 与 `resolvedMistakes` 不是一回事：那个是"学会了"，这个是"那次判定本来就不该算"。
   */
  removedMistakes: number;
  /** 批改后：还没掌握的错词总数。 */
  openMistakeCount: number;
  /** 后端返回的更新后的会话（**字段没拿到时是 `null`**，不是空会话）。 */
  session: DictationSessionDetailView | null;
}

/**
 * 提交批改。
 *
 * ⚠️ **没给到的题按 `correct`**（这是"默认全对，只点错的"在协议层的落点）——
 * 所以调用方必须把**全部题目**的最终判定传进来，而不是只传"改过的那几题"。
 * 那一条不变式由 `buildGradeMarks()`（纯函数，有单测）保证。
 */
export async function gradeSession(
  studentId: string,
  sessionId: string,
  marks: { questionId: string; mark: DictationMarkView }[],
): Promise<GradeResultView> {
  const data = await api.post<unknown>(
    `/students/${studentId}/dictation/sessions/${sessionId}/grade`,
    { marks },
  );
  const r = isRecord(data) ? data : {};
  return {
    sessionId: toStringOrEmpty(r['sessionId']) || sessionId,
    status: toStringOrEmpty(r['status']),
    changedQuestions: toOptionalNumber(r['changedQuestions']) ?? 0,
    unchangedQuestions: toOptionalNumber(r['unchangedQuestions']) ?? 0,
    touchedMistakes: toOptionalNumber(r['touchedMistakes']) ?? 0,
    newMistakes: toOptionalNumber(r['newMistakes']) ?? 0,
    resolvedMistakes: toOptionalNumber(r['resolvedMistakes']) ?? 0,
    removedMistakes: toOptionalNumber(r['removedMistakes']) ?? 0,
    openMistakeCount: toOptionalNumber(r['openMistakeCount']) ?? 0,
    session: isRecord(r['session']) ? decodeSessionDetail(r['session']) : null,
  };
}

// ---------------------------------------------------------------- 端点：错词本（第四步）

/** 错词本里的一条（一条 = 一个词）。 */
export interface MistakeView {
  id: string;
  /** `null` = 这个词已经从字表里删掉了（历史仍在，但不再参与听写）。 */
  wordId: string | null;
  wordText: string;
  pinyin: string;
  wrongCount: number;
  missingCount: number;
  lastWrongAt: string | null;
  lastMarkedAt: string | null;
  nextReviewAt: string | null;
  reviewStage: number;
  reviewIntervalDays: number;
  streakCorrect: number;
  resolvedAt: string | null;
  masteredAt: string | null;
  resolvedBy: 'auto' | 'manual' | null;
  /** 现在是否已到期（**后端算的**；`null` = 没拿到，不猜）。 */
  due: boolean | null;
}

export function decodeMistake(raw: unknown): MistakeView {
  const r = isRecord(raw) ? raw : {};
  const resolvedBy = r['resolvedBy'];
  return {
    id: toStringOrEmpty(r['id']),
    wordId: toOptionalString(r['wordId']),
    wordText: toStringOrEmpty(r['wordText']),
    pinyin: toStringOrEmpty(r['pinyin']),
    wrongCount: toOptionalNumber(r['wrongCount']) ?? 0,
    missingCount: toOptionalNumber(r['missingCount']) ?? 0,
    lastWrongAt: toOptionalString(r['lastWrongAt']),
    lastMarkedAt: toOptionalString(r['lastMarkedAt']),
    nextReviewAt: toOptionalString(r['nextReviewAt']),
    reviewStage: toOptionalNumber(r['reviewStage']) ?? 0,
    reviewIntervalDays: toOptionalNumber(r['reviewIntervalDays']) ?? 0,
    streakCorrect: toOptionalNumber(r['streakCorrect']) ?? 0,
    resolvedAt: toOptionalString(r['resolvedAt']),
    masteredAt: toOptionalString(r['masteredAt']),
    resolvedBy: resolvedBy === 'auto' || resolvedBy === 'manual' ? resolvedBy : null,
    due: toBooleanOrNull(r['due']),
  };
}

/** 错词本列表。`mistakes === null` = **这份数据没拿到**（不是"错词本是空的"）。 */
export async function listMistakes(
  studentId: string,
  query: { dueOnly?: boolean; includeResolved?: boolean; limit?: number; offset?: number } = {},
): Promise<{
  mistakes: MistakeView[] | null;
  total: number | null;
  dueCount: number | null;
  openCount: number | null;
  summary: string | null;
  ladderNote: string | null;
  missing: string[];
}> {
  const data = await api.get<unknown>(`/students/${studentId}/dictation/mistakes`, {
    dueOnly: query.dueOnly === true ? 'true' : 'false',
    includeResolved: query.includeResolved === true ? 'true' : 'false',
    limit: query.limit ?? 100,
    offset: query.offset ?? 0,
  });
  const decoded = decodeList(data, 'mistakes', (raw) => decodeMistake(raw));
  const r = isRecord(data) ? data : {};
  return {
    mistakes: decoded.items,
    total: toOptionalNumber(r['total']),
    dueCount: toOptionalNumber(r['dueCount']),
    openCount: toOptionalNumber(r['openCount']),
    summary: toOptionalString(r['summary']),
    /**
     * 排程规则的人话说明（**后端给的那一句**）。
     *
     * ⚠️ 刻意**不在前端另写一份**：批改页与错词本上出现的规则说明必须逐字一致，
     *   否则家长会以为两处说的是两套规则（"系统是不是在乱排"就是这么来的）。
     *   拿不到时是 `null` —— 界面会显示一个明确的"规则说明没拿到"，
     *   而不是编一句出来。
     */
    ladderNote: toOptionalString(r['ladderNote']),
    missing: decoded.missing,
  };
}

/** 手动「已掌握」/ 撤销。返回后端如实报的三类计数。 */
export async function setMistakeResolved(
  studentId: string,
  wordIds: string[],
  resolved: boolean,
): Promise<{ changed: number; unchanged: number; missing: number }> {
  const data = await api.patch<unknown>(`/students/${studentId}/dictation/mistakes`, {
    wordIds,
    resolved,
  });
  const r = isRecord(data) ? data : {};
  return {
    changed: toOptionalNumber(r['changed']) ?? 0,
    unchanged: toOptionalNumber(r['unchanged']) ?? 0,
    missing: toOptionalNumber(r['missing']) ?? 0,
  };
}

/**
 * 从错词本**直接开始一次听写**。
 *
 * 返回的就是一次普通的会话详情（服务端走的是同一个 `createSession()`），
 * 所以界面拿到它可以直接跳进播放器。
 */
export async function startSessionFromMistakes(
  studentId: string,
  payload: {
    mode?: 'due_only' | 'all';
    limit?: number;
    title?: string;
    tempo?: DictationTempoConfig;
  } = {},
): Promise<DictationSessionDetailView> {
  const data = await api.post<unknown>(
    `/students/${studentId}/dictation/mistakes/session`,
    payload,
  );
  return decodeSessionDetail(data);
}

// ---------------------------------------------------------------- 纯展示辅助（可测）
export const SESSION_STATUS_LABEL: Record<string, string> = {
  pending: '待开始',
  in_progress: '进行中',
  completed: '已完成',
  /** 第四步：家长批改完成。本轮不会出现。 */
  graded: '已批改',
};

export function sessionStatusLabel(status: string): string {
  return status === '' ? '—' : (SESSION_STATUS_LABEL[status] ?? status);
}

/** 播放来源 → 中文。 */
export function spokenSourceLabel(source: SpokenSourceView): string {
  return source === 'parent_audio' ? '家长录音' : '浏览器语音';
}

/**
 * 这段 base64 大概是多少字节。
 *
 * 只在"后端没给 `byteSize`"时用来显示一个约数（正常情况下后端会给出准数）。
 * 拿它就当准数用是不对的 —— 所以界面上要写「约」。
 */
export function approxBytesOfBase64(base64Length: number | null | undefined): number | null {
  if (typeof base64Length !== 'number' || !Number.isFinite(base64Length)) return null;
  return base64DecodedBytes(base64Length);
}
